import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import CMRReport from '@/components/CMRReport'
import { useAutoSave } from '@/hooks/useAutoSave'
import { useAuth } from '@/hooks/useAuth'
import type { ReportRole, Comment } from '@/types'
import toast from 'react-hot-toast'

export default function ReportPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, logout } = useAuth()
  const [role, setRole] = useState<ReportRole>('minister')
  const [status, setStatus] = useState<string>('draft')
  const [ministerName, setMinisterName] = useState<string | undefined>()
  const [reportYear, setReportYear] = useState<string>('2025')
  const [loading, setLoading] = useState(true)
  const [comments, setComments] = useState<Comment[]>([])
  const { data, setInitialData, onChange, saveStatus } = useAutoSave(reportId || null)

  useEffect(() => {
    if (!reportId) return
    const roleParam = searchParams.get('role');
    const url = roleParam ? `/api/reports/${reportId}?role=${roleParam}` : `/api/reports/${reportId}`;
    fetch(url, { credentials: 'include' })
      .then(res => {
        if (res.status === 403) {
          navigate('/dashboard', { replace: true })
          return null
        }
        if (!res.ok) throw new Error('Failed to load report')
        return res.json()
      })
      .then(result => {
        if (!result) return
        if (result.report?.data) {
          setInitialData(result.report.data)
        }
        if (result.role) setRole(result.role)
        if (result.report?.status) setStatus(result.report.status)
        if (result.ministerName) setMinisterName(result.ministerName)
        if (result.report?.reportYear) setReportYear(result.report.reportYear)
      })
      .catch(() => {
        navigate('/dashboard', { replace: true })
      })
      .finally(() => setLoading(false))

    // Load comments
    fetch(`/api/reports/${reportId}/comments`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : { comments: [] })
      .then(res => setComments(res.comments || []))
  }, [reportId, navigate, setInitialData, searchParams])

  const handleInvite = async (section: 'sponsor' | 'mentor', email: string) => {
    const res = await fetch(`/api/reports/${reportId}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ section, email }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || 'Failed to send invite')
    }
    toast.success(`Invite sent to ${email}`)
  }

  const handleAddComment = useCallback(async (sectionId: string, text: string) => {
    const res = await fetch(`/api/reports/${reportId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sectionId, text, role }),
    })
    if (res.ok) {
      const { comment } = await res.json()
      setComments(prev => [...prev, comment])
    } else {
      toast.error('Failed to post comment')
    }
  }, [reportId, role])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f5fc' }}>
        <p style={{ color: '#7c4dba', fontSize: 18, fontFamily: "'Georgia', serif" }}>Loading report...</p>
      </div>
    )
  }

  return (
    <CMRReport
      data={data}
      onChange={onChange}
      saveStatus={saveStatus}
      role={role}
      status={status}
      ministerName={ministerName}
      onInvite={handleInvite}
      onLogout={logout}
      comments={comments}
      onAddComment={handleAddComment}
      reportId={reportId}
      user={user || undefined}
    />
  )
}
